import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Star, UtensilsCrossed } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/review/${token}`).then((r) => setData(r.data)).catch(() => setError("This review link is invalid or has expired."));
  }, [token]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6"><p data-testid="review-error" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-gray-50"><Loader2 className="h-7 w-7 animate-spin text-gray-400" /></div>;

  const accent = data.accent || "#059669";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/public/review/${token}`, { rating, comment });
      setDone(true);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not submit your review.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4" data-testid="review-page">
      <div className="w-full max-w-md bg-white p-8 shadow-sm" style={{ borderTop: `4px solid ${accent}` }}>
        <div className="mb-4 flex h-10 w-10 items-center justify-center" style={{ backgroundColor: accent }}><UtensilsCrossed className="h-5 w-5 text-white" /></div>
        {done || data.already_reviewed ? (
          <div className="text-center" data-testid="review-success">
            <CheckCircle2 className="mx-auto h-10 w-10" style={{ color: accent }} />
            <h1 className="mt-3 text-lg font-bold text-gray-900">Thank you!</h1>
            <p className="mt-1 text-sm text-gray-500">Your review means a lot to {data.company}.</p>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold text-gray-900" data-testid="review-title">How was {data.company}?</h1>
            <p className="mt-1 text-sm text-gray-500">Your event on {data.event_date} — 30 seconds, honest feedback.</p>
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="review-form">
              <div className="flex gap-1.5" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setRating(n)} aria-label={`${n} stars`}>
                    <Star className={`h-8 w-8 transition-colors duration-100 ${rating >= n ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
                  </button>
                ))}
              </div>
              <textarea rows={3} placeholder="Tell us more (optional)…" data-testid="review-comment-input"
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
                value={comment} onChange={(e) => setComment(e.target.value)} />
              {error && <p data-testid="review-form-error" className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              <button type="submit" disabled={!rating || sending} data-testid="review-submit-button"
                className="w-full px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white disabled:opacity-50"
                style={{ backgroundColor: accent }}>
                {sending ? "Sending…" : "Submit review"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
